import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const UsagePage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  // Mock data - in real app, this would come from API
  const usageStats = {
    total_calls: 1247,
    calls_this_month: 847,
    plan_limits: {
      calls_per_month: 1000,
      calls_per_minute: 10,
      calls_per_hour: 100,
    },
    current_usage: {
      calls_this_month: 847,
      calls_today: 45,
      calls_this_hour: 3,
    },
    endpoint_breakdown: [
      { endpoint: '/trends', calls: 456, percentage: 54 },
      { endpoint: '/trends/enhanced', calls: 234, percentage: 28 },
      { endpoint: '/analytics', calls: 89, percentage: 11 },
      { endpoint: '/hashtags', calls: 68, percentage: 8 },
    ],
    cost_breakdown: {
      total_cost: 0,
      cost_this_month: 0,
      projected_cost: 0,
    },
  };

  const apiKeys = [
    {
      id: '1',
      name: 'Production Key',
      key_prefix: 'tb_live_',
      created_at: '2024-01-01T00:00:00Z',
      last_used: '2024-01-15T10:30:00Z',
      status: 'active',
      usage_count: 847,
      error_rate: 0.5,
      response_time: 245,
    },
    {
      id: '2',
      name: 'Development Key',
      key_prefix: 'tb_test_',
      created_at: '2024-01-10T00:00:00Z',
      last_used: '2024-01-14T15:20:00Z',
      status: 'active',
      usage_count: 234,
      error_rate: 2.1,
      response_time: 189,
    },
  ];

  const recentApiCalls = [
    {
      id: '1',
      endpoint: '/trends/enhanced',
      method: 'GET',
      status: 200,
      timestamp: '2024-01-15T10:30:00Z',
      response_time: 245,
      api_key: 'tb_live_***',
    },
    {
      id: '2',
      endpoint: '/trends',
      method: 'GET',
      status: 200,
      timestamp: '2024-01-15T10:25:00Z',
      response_time: 189,
      api_key: 'tb_live_***',
    },
    {
      id: '3',
      endpoint: '/analytics/trends/comparison',
      method: 'GET',
      status: 429,
      timestamp: '2024-01-15T10:20:00Z',
      response_time: 0,
      api_key: 'tb_test_***',
    },
    {
      id: '4',
      endpoint: '/trends',
      method: 'GET',
      status: 200,
      timestamp: '2024-01-15T10:15:00Z',
      response_time: 203,
      api_key: 'tb_live_***',
    },
    {
      id: '5',
      endpoint: '/hashtags',
      method: 'GET',
      status: 200,
      timestamp: '2024-01-15T10:10:00Z',
      response_time: 167,
      api_key: 'tb_live_***',
    },
  ];

  // Chart data for API calls over time
  const chartData = {
    labels: ['Jan 1', 'Jan 3', 'Jan 5', 'Jan 7', 'Jan 9', 'Jan 11', 'Jan 13', 'Jan 15'],
    datasets: [
      {
        label: 'API Calls',
        data: [12, 19, 15, 25, 22, 30, 28, 45],
        borderColor: 'rgb(124, 58, 237)',
        backgroundColor: 'rgba(124, 58, 237, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-600';
    if (status >= 400 && status < 500) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status: number) => {
    if (status >= 200 && status < 300) return <CheckCircleIcon className="h-4 w-4 text-green-600" />;
    if (status >= 400 && status < 500) return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />;
    return <XCircleIcon className="h-4 w-4 text-red-600" />;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
            <p className="text-gray-600 mt-1">
              Monitor your API usage, rate limits, and performance metrics.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none bg-white pr-8"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Usage Overview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Usage Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">API Calls Over Time</h2>
            <div className="h-64">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Plan Limits vs Usage */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Limits & Usage</h2>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Monthly API Calls</span>
                  <span className="text-sm text-gray-600">
                    {usageStats.current_usage.calls_this_month} / {usageStats.plan_limits.calls_per_month}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      usageStats.current_usage.calls_this_month / usageStats.plan_limits.calls_per_month > 0.8
                        ? 'bg-red-600'
                        : 'bg-green-600'
                    }`}
                    style={{
                      width: `${Math.min(
                        (usageStats.current_usage.calls_this_month / usageStats.plan_limits.calls_per_month) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Hourly Rate Limit</span>
                  <span className="text-sm text-gray-600">
                    {usageStats.current_usage.calls_this_hour} / {usageStats.plan_limits.calls_per_hour}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      usageStats.current_usage.calls_this_hour / usageStats.plan_limits.calls_per_hour > 0.8
                        ? 'bg-red-600'
                        : 'bg-green-600'
                    }`}
                    style={{
                      width: `${Math.min(
                        (usageStats.current_usage.calls_this_hour / usageStats.plan_limits.calls_per_hour) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Minute Rate Limit</span>
                  <span className="text-sm text-gray-600">
                    Recent calls / {usageStats.plan_limits.calls_per_minute}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="h-2 rounded-full bg-green-600" style={{ width: '30%' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Endpoint Usage Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Endpoint Usage Breakdown</h2>
            
            <div className="space-y-3">
              {usageStats.endpoint_breakdown.map((endpoint) => (
                <div key={endpoint.endpoint} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-600 rounded-full mr-3" />
                    <span className="text-sm font-medium text-gray-900">{endpoint.endpoint}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">{endpoint.calls} calls</span>
                    <span className="text-sm text-gray-500">{endpoint.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* API Key Usage */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">API Key Usage</h3>
            
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div key={key.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{key.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      key.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {key.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Key:</span>
                      <span className="font-mono text-gray-900">{key.key_prefix}***</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Usage:</span>
                      <span className="text-gray-900">{key.usage_count} calls</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Error Rate:</span>
                      <span className="text-gray-900">{key.error_rate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Response:</span>
                      <span className="text-gray-900">{key.response_time}ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Used:</span>
                      <span className="text-gray-900">
                        {new Date(key.last_used).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Limit Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Limit Status</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">Monthly Limit</span>
                </div>
                <span className="text-sm text-green-600">Good</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">Hourly Limit</span>
                </div>
                <span className="text-sm text-green-600">Good</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-800">Minute Limit</span>
                </div>
                <span className="text-sm text-green-600">Good</span>
              </div>
            </div>
          </div>

          {/* Usage Alerts */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Alerts</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">80% Usage Alert</span>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="toggle-80"
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    defaultChecked
                  />
                  <label htmlFor="toggle-80" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">90% Usage Alert</span>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="toggle-90"
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    defaultChecked
                  />
                  <label htmlFor="toggle-90" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">100% Usage Alert</span>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="toggle-100"
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                    defaultChecked
                  />
                  <label htmlFor="toggle-100" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent API Calls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent API Calls</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  API Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentApiCalls.map((call) => (
                <tr key={call.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {call.endpoint}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(call.status)}
                      <span className={`ml-2 text-sm font-medium ${getStatusColor(call.status)}`}>
                        {call.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {call.response_time}ms
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {call.api_key}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(call.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsagePage; 